<template>
  <MainLayout :pages="pagesList">
    <div v-if="loading" class="p-4 border-round bg-blue-50 mb-3">
    <Message severity="info" :closable="false">
        <template #detail>Loading page...</template>
    </Message>
    </div>
    <div v-else-if="pagesList">
        <Carousel
            ref="carousel"
            v-model:page="currentPageIndex"
            :value="pagesList"
            :numVisible="1"
            :showIndicators="false"
            :showNavigators="false"
            :pt="{ viewport: 'h-[100vh]', item: '!visible h-[100vh]' }">
            <template #item="slotProps">
                <component
                    :is="layoutMap[slotProps.data.layout]"
                    :page="slotProps.data"
                    :settings="settings"
                    :strapiUrl="baseUrl"
                    :is-active="!!isContentsPage(slotProps.data, index)"
                    :key="slotProps.data?.id ?? $route.fullPath"
                    class="pt-carousel-content"
                />
            </template>
        </Carousel>
    </div>
    <div v-else class="p-4 border-round bg-yellow-50 mb-3">
    <Message severity="warn" :closable="false">
        <template #detail>Page not found.</template>
    </Message>
    </div>
  </MainLayout>
</template>

<script setup>
import { onMounted, nextTick, ref, computed, watch } from 'vue';
import MainLayout from '../Layouts/MainLayout.vue';
import Message from 'primevue/message';
import Carousel from 'primevue/carousel';
import LandingLayout from '../Layouts/LandingLayout.vue';
import ContentsLayout from '../Layouts/ContentsLayout.vue';
import FeaturedLayout from '../Layouts/FeaturedLayout.vue';
import TabbedLayout from '../Layouts/TabbedLayout.vue';
import AltFeaturedLayout from '../Layouts/AltFeaturedLayout.vue';
import ContactLayout from '../Layouts/ContactLayout.vue';
import IsotopesLayout from '../Layouts/IsotopesLayout.vue';
import DoubleLayout from '../Layouts/DoubleLayout.vue';
import { useStrapiUrl } from '../composables/useStrapiUrl.js';
import { useNavigation } from '../composables/useNavigation.js';
import { setupCarouselLinks } from '../utils/linkHandler.js';
import { useRoute } from 'vue-router';

// Use centralized Strapi URL management
const { baseUrl } = useStrapiUrl();
const carousel = ref(null);
const route = useRoute();

const layoutMap = {
  landing: LandingLayout,
  contents: ContentsLayout,
  featured: FeaturedLayout,
  double: DoubleLayout,
  alternate: AltFeaturedLayout,
  contact: ContactLayout,
  isotopes: IsotopesLayout,
  tabbed: TabbedLayout,
};

const { pagesList, loading, settings, currentPageIndex, scrollToTop, updateCarouselScrolling} = useNavigation();

// Find the index of the contents page
const contentsPageIndex = computed(() => {
  if (!pagesList.value) return -1;
  return pagesList.value.findIndex(page => page.layout === 'contents');
});

// Check if the current slide is the contents page
const isContentsPage = (page, index) => {
  const isContents = page.layout === 'contents' || index === contentsPageIndex.value;
  return isContents;
};

onMounted(() => {
    scrollToTop();
    if (carousel.value) {
      carousel.value.swipeThreshold = 200; // effectively reduces accidental swipes
      // If you want to *disable* swipe entirely, set a huge number:
      // carousel.value.swipeThreshold = Number.MAX_SAFE_INTEGER;
    }
    
    nextTick(() => {
        setupCarouselLinks();
        window.addEventListener('resize', updateCarouselScrolling);
        
        // Check if we're on a non-home route and slide to current page
        const currentSlug = route.params.slug;
        const isHomeRoute = currentSlug === '1';
        
        if (!isHomeRoute && currentSlug) {
            // Force a re-render of the carousel to ensure it displays the correct slide
            setTimeout(() => {
                if (carousel.value && pagesList.value && pagesList.value.length > 0) {
                    carousel.value.$forceUpdate?.();
                    // Also trigger a resize event which often helps carousels re-render
                    nextTick(() => {
                        window.dispatchEvent(new Event('resize'));
                    });
                }
            }, 100);
        } 
    });
});
</script>